import { Button } from '@/components/ui/Button';
import { isApiError } from '@/api/errors';
import { ExclamationTriangleIcon, ArrowPathIcon, LockClosedIcon, NoSymbolIcon } from '@/icons';

/**
 * Standard error surface for failed queries. Derives a friendly title from the
 * error kind (auth/permission/unavailable/etc.) and offers a retry when given.
 */
export function ErrorState({ error, onRetry, compact = false }: { error: unknown; onRetry?: () => void; compact?: boolean }) {
  let title = 'Something went wrong';
  let description = 'An unexpected error occurred. Please try again.';
  let Icon = ExclamationTriangleIcon;

  if (isApiError(error)) {
    switch (error.kind) {
      case 'forbidden': title = 'Access denied'; description = "You don't have permission to view this."; Icon = LockClosedIcon; break;
      case 'unauthenticated': title = 'Session expired'; description = 'Please sign in again.'; Icon = LockClosedIcon; break;
      case 'downstream_unavailable': title = 'Temporarily unavailable'; description = 'This service is degraded. Try again shortly.'; Icon = NoSymbolIcon; break;
      case 'not_found': title = 'Not found'; description = 'The requested item could not be found.'; break;
      case 'network': title = 'Connection problem'; description = 'Nifty Hosting could not be reached. Check your connection and try again.'; Icon = NoSymbolIcon; break;
      case 'rate_limited': title = 'Too many requests'; description = 'Please wait a moment and try again.'; break;
      default: description = error.message || description;
    }
  }

  return (
    <div className={compact ? 'flex flex-col items-center gap-2 py-6 text-center' : 'flex flex-col items-center gap-3 py-14 text-center'}>
      <span className="grid h-12 w-12 place-items-center rounded-2xl bg-danger/10 text-danger">
        <Icon className="h-6 w-6" aria-hidden="true" />
      </span>
      <div>
        <p className="text-sm font-semibold text-fg">{title}</p>
        <p className="mt-1 max-w-sm text-sm text-fg-muted">{description}</p>
      </div>
      {onRetry && (
        <Button size="sm" variant="flat" startContent={<ArrowPathIcon className="h-4 w-4" />} onPress={onRetry}>Try again</Button>
      )}
    </div>
  );
}
